import {
  IsSort,
  Table,
  TableBody,
  TableCell,
  TableCellHead,
  TableHead,
  TableRow,
} from '~/ui/Table';
import { IconButton, IconButtonLink } from '~/ui';
import Edit from '~/assets/svg/newSvg/edit.svg?react';
import styles from './FullMenuTable.module.scss';
import { sanitizeMeasure } from '../utils';
import { Photo } from '~/components/UploadPhoto/UploadPhoto';
import { useState } from 'react';
import { Modal } from '~/components/Shared/Modal/Modal';
import NoImageIcon from '~/assets/svg/newSvg/empty-image.svg?react';
import cn from 'classnames';
import ImageContainer from './ImageContainer/ImageContainer';
import TooltipText from '~/components/Shared/TooltipText/TooltipText';
import { useTranslation } from 'react-i18next';
import { getPaginate } from '~/utils/getPaginate';
import Eye from '~/assets/svg/newSvg/eye.svg?react';
import EyeHidden from '~/assets/svg/newSvg/eye-hidden.svg?react';
import SpinnerCircle from '~/ui/SpinnerCircle/SpinnerCircle';

interface MenuItem {
  id: number;
  wares_name: string;
  price_list: { price: string; price_list_name: string };
  weight: string;
  dish_type: string;
  code: string;
  photos: Photo[];
  measure: string;
  is_visible: boolean;
}

interface FeedbacksTableProps {
  menu: MenuItem[];
  sort: IsSort;
  setSort: ({ field, direction }: IsSort) => void;
  foodId: number;
  updateVisibility: (id: number, isVisible: boolean) => void;
  isUpdateVisibilityLoading: boolean;
  changingId: number | null;
}

export const FullMenuTable = ({
  menu,
  sort,
  setSort,
  foodId,
  updateVisibility,
  isUpdateVisibilityLoading,
  changingId,
}: FeedbacksTableProps): JSX.Element => {
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const { value, paginate } = getPaginate();
  const { value: foodValue, paginate: foodPaginate } = getPaginate('foodMenu');

  const clickPreviewImage = (image: string) => {
    setModalIsOpen(true);
    setSelectedImage(image);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedImage('');
  };

  const handleUpdateVisibility = (id: number, isVisible: boolean) => {
    updateVisibility(id, isVisible);
  };

  return (
    <div className={styles.tableWrapper}>
      <Table>
        <TableHead sort={sort} setSort={setSort}>
          <TableCellHead field="wares_name">{t('name')}</TableCellHead>
          <TableCellHead field="code">{t('dish_code')}</TableCellHead>
          <TableCellHead field="price_list">{t('price_list')}</TableCellHead>
          <TableCellHead field="weight">{t('measure')}</TableCellHead>
          <TableCellHead field="photo" sortable={false}>
            {t('Photo')}
          </TableCellHead>
          <TableCellHead sortable={false} field="actions">
            {t('action')}
          </TableCellHead>
        </TableHead>
        <TableBody>
          {menu.map(
            ({
              id,
              wares_name,
              code,
              price_list,
              weight,
              photos,
              measure,
              is_visible,
            }: MenuItem) =>
              changingId === id ? (
                <TableRow>
                  <TableCell>
                    <SpinnerCircle />
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={id}>
                  <TableCell className={styles.chadgingCell}>
                    <TooltipText text={wares_name} />
                  </TableCell>
                  <TableCell className={styles.foodCell}>{code}</TableCell>
                  <TableCell className={styles.foodPriceList}>
                    <TooltipText text={price_list?.price_list_name} />
                  </TableCell>
                  <TableCell className={cn(styles.foodCell, styles.weightCell)}>
                    <span>
                      {sanitizeMeasure(weight)} {measure.toLowerCase()}
                    </span>
                  </TableCell>
                  <TableCell className={styles.foodCellImage}>
                    {photos?.length ? (
                      <ImageContainer
                        onClick={() =>
                          clickPreviewImage(photos[0]?.full_photo_url)
                        }
                        image={photos[0]?.full_photo_url}
                      />
                    ) : (
                      <div className={styles.foodCellImageEmpty}>
                        <NoImageIcon />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className={styles.editFoodTableActions}>
                      <IconButtonLink
                        to={`/food/${foodId}/menu/${id}?${value}=${paginate}&${foodValue}=${foodPaginate}`}
                        className={styles.editFoodTableBtnLink}
                      >
                        <Edit />
                      </IconButtonLink>
                      {isUpdateVisibilityLoading ? (
                        <SpinnerCircle />
                      ) : (
                        <IconButton
                          onClick={() =>
                            handleUpdateVisibility(id, !is_visible)
                          }
                        >
                          {is_visible ? <Eye /> : <EyeHidden />}
                        </IconButton>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
          )}
        </TableBody>
        <Modal
          modalStyles={styles.foodModal}
          isOpen={modalIsOpen}
          onClose={closeModal}
          isCloseButtonShown
        >
          <img className={styles.foodModalImage} src={selectedImage} />
        </Modal>
      </Table>
    </div>
  );
};
